import { isTouchDevice, keys, G, MOBILE_ZOOM } from '../state';

let overlay: HTMLDivElement | null = null;
let instructionEl: HTMLDivElement | null = null;
let instrTimer = 0;
let instrVisible = false;

function makeBtn(label: string, css: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.style.cssText = [
    'position:absolute',
    'font-family:Courier New,monospace',
    'font-size:13px',
    'font-weight:bold',
    'color:#ffffff',
    'background:rgba(0,0,20,0.55)',
    'border:2px solid rgba(100,180,255,0.7)',
    'border-radius:8px',
    'pointer-events:auto',
    'touch-action:none',
    'user-select:none',
    '-webkit-user-select:none',
    'cursor:pointer',
    css,
  ].join(';');
  return b;
}

function holdBtn(
  label: string,
  css: string,
  onDown: () => void,
  onUp: () => void,
): HTMLButtonElement {
  const b = makeBtn(label, css);
  b.addEventListener('pointerdown', (e) => { e.stopPropagation(); onDown(); });
  b.addEventListener('pointerup',   (e) => { e.stopPropagation(); onUp(); });
  b.addEventListener('pointerleave',(e) => { e.stopPropagation(); onUp(); });
  b.addEventListener('pointercancel',(e) => { e.stopPropagation(); onUp(); });
  return b;
}

function tapBtn(
  label: string,
  css: string,
  onTap: () => void,
): HTMLButtonElement {
  const b = makeBtn(label, css);
  b.addEventListener('pointerdown', (e) => { e.stopPropagation(); onTap(); });
  return b;
}

// ── Virtual joystick ──────────────────────────────────────────────────────────
const JOY_RADIUS = 52;
const JOY_KNOB   = 22;
const JOY_MAX    = 44;

let joyPointerId: number | null = null;
let joyBaseX = 0, joyBaseY = 0;

function buildJoystick(parent: HTMLDivElement): void {
  const base = document.createElement('div');
  base.style.cssText = [
    'position:fixed',
    `width:${JOY_RADIUS * 2}px`,
    `height:${JOY_RADIUS * 2}px`,
    `border-radius:${JOY_RADIUS}px`,
    'background:rgba(0,180,255,0.08)',
    'border:2px solid rgba(0,200,255,0.30)',
    'pointer-events:none',
    'display:none',
    'z-index:201',
  ].join(';');

  const knob = document.createElement('div');
  knob.style.cssText = [
    'position:absolute',
    `width:${JOY_KNOB * 2}px`,
    `height:${JOY_KNOB * 2}px`,
    `border-radius:${JOY_KNOB}px`,
    'background:rgba(0,220,255,0.25)',
    'border:2px solid rgba(0,220,255,0.65)',
    'pointer-events:none',
    'transform:translate(-50%,-50%)',
    'left:50%', 'top:50%',
  ].join(';');

  base.appendChild(knob);
  document.body.appendChild(base);

  // Touch capture zone — left 48% of screen
  const zone = document.createElement('div');
  zone.style.cssText = [
    'position:fixed',
    'left:0', 'top:0',
    'width:48%', 'height:100%',
    'pointer-events:auto',
    'touch-action:none',
    'z-index:199',
    'display:none',
  ].join(';');
  parent.appendChild(zone);

  // expose zone so show/hide can control it
  (parent as any)._joyZone = zone;
  (parent as any)._joyBase = base;

  function positionBase(sx: number, sy: number): void {
    joyBaseX = sx; joyBaseY = sy;
    base.style.left = `${sx - JOY_RADIUS}px`;
    base.style.top  = `${sy - JOY_RADIUS}px`;
    base.style.display = '';
  }

  function moveKnob(dx: number, dy: number): void {
    const dist = Math.hypot(dx, dy);
    const clamp = Math.min(dist, JOY_MAX);
    const nx = dist > 0 ? dx / dist : 0;
    const ny = dist > 0 ? dy / dist : 0;
    knob.style.left = `${JOY_RADIUS + nx * clamp}px`;
    knob.style.top  = `${JOY_RADIUS + ny * clamp}px`;
    keys.joyX = nx * Math.min(1, dist / JOY_MAX);
    keys.joyY = ny * Math.min(1, dist / JOY_MAX);
  }

  function releaseJoy(): void {
    joyPointerId = null;
    keys.joyX = 0; keys.joyY = 0;
    base.style.display = 'none';
    knob.style.left = '50%'; knob.style.top = '50%';
  }

  zone.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    if (joyPointerId !== null) return;
    joyPointerId = e.pointerId;
    zone.setPointerCapture(e.pointerId);
    positionBase(e.clientX, e.clientY);
    moveKnob(0, 0);
  });
  zone.addEventListener('pointermove', (e) => {
    e.stopPropagation();
    if (e.pointerId !== joyPointerId) return;
    moveKnob(e.clientX - joyBaseX, e.clientY - joyBaseY);
  });
  zone.addEventListener('pointerup',     (e) => { e.stopPropagation(); if (e.pointerId === joyPointerId) releaseJoy(); });
  zone.addEventListener('pointercancel', (e) => { e.stopPropagation(); if (e.pointerId === joyPointerId) releaseJoy(); });
}

// ── Right-side aim + fire zone ────────────────────────────────────────────────
function buildAimZone(parent: HTMLDivElement): void {
  const zone = document.createElement('div');
  zone.style.cssText = [
    'position:fixed',
    'right:0', 'top:0',
    'width:52%', 'height:70%',
    'pointer-events:auto',
    'touch-action:none',
    'z-index:199',
    'display:none',
  ].join(';');
  parent.appendChild(zone);
  (parent as any)._aimZone = zone;

  let aimPtr: number | null = null;

  zone.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    if (aimPtr === null) aimPtr = e.pointerId;
    zone.setPointerCapture(e.pointerId);
    G.mouse.x = e.clientX / MOBILE_ZOOM;
    G.mouse.y = e.clientY / MOBILE_ZOOM;
    G.mouse.down = true;
  });
  zone.addEventListener('pointermove', (e) => {
    e.stopPropagation();
    if (e.pointerId !== aimPtr) return;
    G.mouse.x = e.clientX / MOBILE_ZOOM;
    G.mouse.y = e.clientY / MOBILE_ZOOM;
  });
  zone.addEventListener('pointerup', (e) => {
    e.stopPropagation();
    if (e.pointerId === aimPtr) { aimPtr = null; G.mouse.down = false; }
  });
  zone.addEventListener('pointercancel', (e) => {
    e.stopPropagation();
    if (e.pointerId === aimPtr) { aimPtr = null; G.mouse.down = false; }
  });
}

// ── Instructions banner ───────────────────────────────────────────────────────
function buildInstructions(parent: HTMLDivElement): void {
  instructionEl = document.createElement('div');
  instructionEl.innerHTML = [
    '<b style="font-size:15px;letter-spacing:3px;color:#ffffff">CONTRÔLES</b><br>',
    '🕹 JOYSTICK gauche — déplacer<br>',
    '👆 DROITE — viser · maintenir pour tirer<br>',
    '🔴 FREIN &nbsp; ⚡ BOOST',
  ].join('');
  instructionEl.style.cssText = [
    'position:fixed',
    'top:50%', 'left:50%',
    'transform:translate(-50%,-50%)',
    'background:rgba(0,0,20,0.88)',
    'border:1.5px solid rgba(0,200,255,0.5)',
    'border-radius:12px',
    'padding:18px 28px',
    'color:#00ffff',
    'font-family:Courier New,monospace',
    'font-size:13px',
    'letter-spacing:2px',
    'text-align:center',
    'line-height:2',
    'pointer-events:none',
    'z-index:300',
    'display:none',
    'transition:opacity 0.6s',
  ].join(';');
  parent.appendChild(instructionEl);
}

export function showMobileInstructions(): void {
  if (!instructionEl || !isTouchDevice) return;
  instructionEl.style.display = 'block';
  instructionEl.style.opacity = '1';
  instrVisible = true;
  instrTimer = 5;
}

export function tickMobileInstructions(dt: number): void {
  if (!instructionEl || !instrVisible) return;
  instrTimer -= dt;
  if (instrTimer <= 0 || G.mouse.down) {
    instrTimer = 0;
    instrVisible = false;
    instructionEl.style.opacity = '0';
    setTimeout(() => { if (instructionEl) instructionEl.style.display = 'none'; }, 650);
  }
}

// ── Main init ─────────────────────────────────────────────────────────────────
export function initTouchControls(): void {
  if (!isTouchDevice) return;

  overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:200;';

  buildJoystick(overlay);
  buildAimZone(overlay);
  buildInstructions(overlay);

  const brakeBtn = holdBtn(
    'FREIN',
    'bottom:12px;right:12px;width:76px;height:76px;border-color:rgba(255,200,60,0.8);',
    () => { keys.brake = true; },
    () => { keys.brake = false; },
  );

  const boostBtn = holdBtn(
    'BOOST',
    'bottom:100px;right:12px;width:76px;height:76px;border-color:rgba(60,160,255,0.8);',
    () => { keys.boost = true; },
    () => { keys.boost = false; },
  );

  const spLabels = ['[1]\nBOMBE', '[2]\nSURTEN', '[3]\nRALENTI'];
  const spCols   = ['rgba(255,170,0,0.8)', 'rgba(68,136,255,0.8)', 'rgba(255,255,68,0.8)'];
  const spBtns = spLabels.map((lbl, i) =>
    tapBtn(
      lbl,
      `bottom:12px;left:calc(50% - 118px + ${i * 84}px);width:78px;height:52px;white-space:pre-line;line-height:1.2;border-color:${spCols[i]};`,
      () => { keys.touchSpecial = i; },
    ),
  );

  overlay.appendChild(brakeBtn);
  overlay.appendChild(boostBtn);
  spBtns.forEach((b) => overlay!.appendChild(b));
  document.body.appendChild(overlay);

  // Show/hide zones + buttons based on game state
  requestAnimationFrame(function tick() {
    if (overlay) {
      const playing = G.state === 'playing';
      brakeBtn.style.display = playing ? '' : 'none';
      boostBtn.style.display = playing ? '' : 'none';
      spBtns.forEach((b) => { b.style.display = playing ? '' : 'none'; });
      const joyZone = (overlay as any)._joyZone as HTMLDivElement | undefined;
      const aimZone = (overlay as any)._aimZone as HTMLDivElement | undefined;
      const joyBase = (overlay as any)._joyBase as HTMLDivElement | undefined;
      if (joyZone) joyZone.style.display = playing ? '' : 'none';
      if (aimZone) aimZone.style.display = playing ? '' : 'none';
      if (!playing && joyBase) joyBase.style.display = 'none';
    }
    requestAnimationFrame(tick);
  });
}
